import { CrimsonCls, Emojis, PLAYER_ICONS, ValueCls } from 'constants/constants';
import {
  FbSqUser,
  SqGame,
  SqGameID,
  SqGameProgress,
  SqGameState,
  SqPicksKind,
  SqPlayer,
  SqPlayerMarker,
  SqPlayerPick,
  SqScoringOption,
  SqTeamScoringDetails,
  SqUserID,
} from 'types/SqGame.types';

// export const displayIcons = ['⓪', '①', '②', '③', '④', '⑤', '⑥', '⑦', '⑧', '⑨'];
export const displayIcons = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
export const END_DATE = new Date('Sun Feb 08 2026 15:30:00 GMT-0800').getTime();

export const createIncrementingArray = (length: number): number[] => {
  return Array.from({ length }, (_, index) => index);
};

export const createEmptyStringArray = (length: number): string[] => {
  return Array.from({ length }, () => '');
};

export const generateRandomArray = (): number[] => {
  const array = createIncrementingArray(10);
  return shuffleArray(array);
};

export const getDefaultPlayerMarker = (markerIndex: number = 0): SqPlayerMarker => {
  return { markerIndex, markerValue: PLAYER_ICONS[markerIndex] };
};

export const getInitialSqPlayer = (
  user: FbSqUser,
  isAdmin: boolean = false,
  numOfPicks: number = 0,
  markerIndex: number = 0,
  gameId: string = ''
): SqPlayer => {
  return {
    id: user.uid,
    name: user.name,
    numberOfPicks: numOfPicks,
    picks: [],
    winningQuarters: [],
    marker: getDefaultPlayerMarker(markerIndex),
    createdOn: Date.now(),
    gameId: gameId,
    isAdmin,
    isBanned: false,
  };
};

export const initialSquareGame = (title = 'New Game', user: FbSqUser, matchId: string = ''): SqGame => {
  const dateMS = Date.now();
  const date = new Date(dateMS);
  const picks = createEmptyStringArray(100);
  const startingDate: number = END_DATE;

  return {
    sqGameId: '',
    matchId,
    title,
    description: 'n/a',
    url: 'https://squares.eleventheye.com/game/{gameId}',
    players: [],
    picks,
    playerIds: [],
    openSquares: 100,
    gameProgress: SqGameProgress.NotStarted,
    gameState: SqGameState.Picks,
    homeTeam: {
      name: 'HomeTeam',
      id: '0',
      randomNumbers: [],
      logoUrl: '/images/logos/no-team-logo.webp',
      pointsAllowed: 0,
      pointsScored: 0,
      displayPosition: 'horizontal',
    },
    awayTeam: {
      name: 'AwayTeam',
      id: '0',
      randomNumbers: [],
      logoUrl: '/images/logos/no-team-logo.webp',
      pointsAllowed: 0,
      pointsScored: 0,
      displayPosition: 'vertical',
    },
    date: date.toISOString(),
    startingDate,
    boardLocked: false,
    gameAdmin: { id: user.uid, name: user.name },
    pickingPlayer: '',
    lastPickingPlayer: '',
    quarterScores: [],
    quarterWinners: [],
    bannedPlayerIds: [],
    maxPicksPerPlayer: 0,
    createdBy: user.name,
    createdOn: dateMS,
  };
};

export const getGameUrl = (gameId: string): string => `https://squares.eleventheye.com/game/${gameId}`;

export const getNextUnusedMarker = (players: SqPlayer[]): SqPlayerMarker => {
  const usedMarkers = players.map((p) => p.marker.markerIndex);
  const index = PLAYER_ICONS.findIndex((_, index) => !usedMarkers.includes(index));
  return getMarker(index);
};

export const getMarker = (index: number): SqPlayerMarker => {
  return { markerIndex: index, markerValue: PLAYER_ICONS[index] };
};

// #region SCORING BREAKDOWN CALCULATIONS *************************************
const TD = 6;
const FG = 3;
const SAFETY = 2;
const PAT = 1;
const PAT_2 = 2;

const translateNumbersToScoring = (scoringPts: number[]): string => {
  return scoringPts
    .map((pt, index, array) => {
      switch (pt) {
        case TD:
          return SqScoringOption.TD;
        case PAT:
          return SqScoringOption.PAT;
        case PAT_2:
          return index > 0 && array[index - 1] === TD ? SqScoringOption.PAT2 : SqScoringOption.SF;
        case FG:
          return SqScoringOption.FG;
        case SAFETY:
          return SqScoringOption.SF;
        default:
          return SqScoringOption.NONE;
      }
    })
    .join(', ');
};

const calculateScoringNeededWithSafety = (ptToScore: number, scoringPts: number[]): number[] => {
  // const msg = `${Emojis.eyes} %c ptToScore: ${ptToScore} %c %c scoringPts: ${scoringPts.toString()} %c`;
  // console.log(msg, ValueCls, '', ValueCls, '');

  if (ptToScore === 0) {
    // console.log(`${Emojis.eyes} returning %c scoringPts: ${scoringPts.toString()} %c`, ValueCls, '');
    return scoringPts;
  }

  if (ptToScore - TD >= 0) {
    return calculateScoringNeededWithSafety(ptToScore - TD, [...scoringPts, TD]);
  } else if (scoringPts.length > 0 && scoringPts[scoringPts.length - 1] === 6 && ptToScore - PAT >= 0) {
    return calculateScoringNeededWithSafety(ptToScore - PAT, [...scoringPts, PAT]);
  } else if (scoringPts.length > 0 && scoringPts[scoringPts.length - 1] === 6 && ptToScore - PAT_2 >= 0) {
    return calculateScoringNeededWithSafety(ptToScore - PAT_2, [...scoringPts, PAT_2]);
  } else if (ptToScore - FG >= 0) {
    return calculateScoringNeededWithSafety(ptToScore - FG, [...scoringPts, FG]);
  } else if (ptToScore - SAFETY >= 0) {
    return calculateScoringNeededWithSafety(ptToScore - SAFETY, [...scoringPts, SAFETY]);
  } else if (scoringPts.length > 0 && scoringPts[scoringPts.length - 1] === PAT && ptToScore + PAT - PAT_2 >= 0) {
    // remove previous PAT and add PAT_2
    return calculateScoringNeededWithSafety(ptToScore - PAT, [...scoringPts.slice(0, scoringPts.length - 1), PAT_2]);
  } else if (scoringPts.length > 0 && scoringPts.toString() === [6, 1, 3].toString()) {
    return calculateScoringNeededWithSafety(3, [6, PAT_2]);
  }

  return [];
};

const calculateScoringNeededWithoutSafety = (ptToScore: number, scoringPts: number[]): number[] => {
  // const msg = `${Emojis.eyes} %c ptToScore: ${ptToScore} %c %c scoringPts: ${scoringPts.toString()} %c`;
  // console.log(msg, ValueCls, '', ValueCls, '');

  if (ptToScore === 0) {
    // console.log(`${Emojis.eyes} returning %c scoringPts: ${scoringPts.toString()} %c`, ValueCls, '');
    return scoringPts;
  }

  if (ptToScore - TD >= 0) {
    return calculateScoringNeededWithoutSafety(ptToScore - TD, [...scoringPts, TD]);
  } else if (scoringPts.length > 0 && scoringPts[scoringPts.length - 1] === 6 && ptToScore - PAT >= 0) {
    return calculateScoringNeededWithoutSafety(ptToScore - PAT, [...scoringPts, PAT]);
  } else if (scoringPts.length > 0 && scoringPts[scoringPts.length - 1] === 6 && ptToScore - PAT_2 >= 0) {
    return calculateScoringNeededWithoutSafety(ptToScore - PAT_2, [...scoringPts, PAT_2]);
  } else if (ptToScore - FG >= 0) {
    return calculateScoringNeededWithoutSafety(ptToScore - FG, [...scoringPts, FG]);
  } else if (scoringPts.length > 0 && scoringPts[scoringPts.length - 1] === PAT && ptToScore + PAT - PAT_2 >= 0) {
    // remove previous PAT and add PAT_2
    return calculateScoringNeededWithoutSafety(ptToScore - PAT, [...scoringPts.slice(0, scoringPts.length - 1), PAT_2]);
  } else if (scoringPts.length > 0 && scoringPts.toString() === [6, 1, 3].toString()) {
    return calculateScoringNeededWithoutSafety(3, [6, PAT_2]);
  } else if (scoringPts.length > 0 && scoringPts[scoringPts.length - 1] === PAT_2 && ptToScore + PAT_2 - FG >= 0) {
    // remove previous PAT_2 and add FG
    return calculateScoringNeededWithoutSafety(ptToScore - PAT, [...scoringPts.slice(0, scoringPts.length - 1), FG]);
  }

  return [];
};

export const calculatePointsNeeded = (
  score: number,
  value: number,
  doNotUseSafety: boolean = false
): SqTeamScoringDetails => {
  // const numOf10 = Math.floor(score / 10);
  const remainder = score % 10;
  if (remainder === value) {
    return {
      pointsNeeded: 0,
      scoreNeeded: score,
      scoringOptions: [SqScoringOption.NONE],
    };
  }

  let scoringNeeded: number[] = [];
  let diff = 0;
  if (remainder > value) {
    diff = 10 - (remainder - value);
  } else {
    diff = value - remainder;
  }

  // console.log(`${Emojis.eyes} %c diff: ${diff} %c`, ValueCls, '');
  if (doNotUseSafety) {
    const updatedDiff = diff === 1 ? 11 : diff === 2 ? 12 : diff === 4 ? 14 : diff === 5 ? 15 : diff;
    scoringNeeded = calculateScoringNeededWithoutSafety(updatedDiff, []);
  } else {
    scoringNeeded = calculateScoringNeededWithSafety(diff === 1 ? 11 : diff, []);
  }
  // console.log(`${Emojis.eyes} %c scoringNeeded: ${scoringNeeded.toString()} %c`, ValueCls, '');
  const sum = scoringNeeded.reduce((acc, curr) => acc + curr, 0);
  const newScore = sum + score;

  const teamScoring: SqTeamScoringDetails = {
    pointsNeeded: sum,
    scoreNeeded: newScore,
    scoringOptions: translateNumbersToScoring(scoringNeeded).split(', ') as SqScoringOption[],
  };

  // return `You need this team to score ${sum} more points to get to the ${newScore} points. This will take a ${translateNumbersToScoring(scoringNeeded)}`;
  return teamScoring;
};

export const getNeededScoring = (
  scoreHome: number,
  scoreAway: number,
  homeValue: number,
  awayValue: number,
  withoutSafety: boolean = false
): SqTeamScoringDetails[] => {
  let needHomeScore: boolean = false;
  let needAwayScore: boolean = false;

  let homeTeamAnswer: SqTeamScoringDetails = {
    pointsNeeded: 0,
    scoreNeeded: scoreHome,
    scoringOptions: [SqScoringOption.NONE],
  };

  let awayTeamAnswer: SqTeamScoringDetails = {
    pointsNeeded: 0,
    scoreNeeded: scoreAway,
    scoringOptions: [SqScoringOption.NONE],
  };

  if (scoreHome % 10 === homeValue && scoreAway % 10 === awayValue) {
    return [homeTeamAnswer, awayTeamAnswer];
  }

  if (scoreHome % 10 !== homeValue) {
    needHomeScore = true;
  }

  if (scoreAway % 10 !== awayValue) {
    needAwayScore = true;
  }

  if (needHomeScore && needAwayScore) {
    homeTeamAnswer = calculatePointsNeeded(scoreHome, homeValue, withoutSafety);
    awayTeamAnswer = calculatePointsNeeded(scoreAway, awayValue, withoutSafety);
    // const generalMsg = 'You need both a home and away score.';
    // return `${generalMsg} ${homeTeamAnswer}. ${awayTeamAnswer}.`;
    return [homeTeamAnswer, awayTeamAnswer];
  }

  if (needHomeScore) {
    homeTeamAnswer = calculatePointsNeeded(scoreHome, homeValue, withoutSafety);
    // const generalMsg = 'You need a home score';

    // return `${generalMsg} ${homeTeamAnswer}.`;
    return [homeTeamAnswer, awayTeamAnswer];
  }

  if (needAwayScore) {
    const awayTeamAnswer = calculatePointsNeeded(scoreAway, awayValue, withoutSafety);
    // const generalMsg = 'You need an away score';

    // return `${generalMsg} ${awayTeamAnswer}.`;
    return [homeTeamAnswer, awayTeamAnswer];
  }

  return [homeTeamAnswer, awayTeamAnswer];
};
// #endregion

// region
const populatePicksListWithPlayerMarkers = (picks: SqPicksKind, playerPicks: SqPlayerPick[]) => {
  const copyPicks = picks.slice();
  playerPicks.forEach((p: SqPlayerPick) => {
    if (copyPicks[p.pick] === '') {
      copyPicks[p.pick] = p;
    } else {
      const inList: SqPlayerPick = copyPicks[p.pick] as SqPlayerPick;
      const { pick, playerID, marker } = inList;
      const msgInList = `%c Pick: ${pick} %c, %c uid:${playerID} %c, %c Marker: ${marker.markerValue} %c`;
      const msgPassIn = `%c Pick: ${p.pick} %c, %c uid:${p.playerID} %c, %c Marker: ${p.marker.markerValue} %c`;
      console.log(`${Emojis.warning} %c Duplicate Entry Found %c `, CrimsonCls, '');
      console.log(`${Emojis.warning} Exists in list ${msgInList} `, ValueCls, '', ValueCls, '', ValueCls, '');
      console.log(`${Emojis.warning} Passed in prop ${msgPassIn}`, ValueCls, '', ValueCls, '', ValueCls, '');
      console.log(`${Emojis.warning} ==================================================== `);
    }
  });

  return copyPicks;
};

export const generatePicksList = (players: SqPlayer[]): SqPicksKind => {
  let picks: SqPicksKind = createEmptyStringArray(100);
  players.forEach((pl) => {
    const plPicks: SqPlayerPick[] = pl.picks.map(
      (p) =>
        ({
          pick: p,
          playerID: pl.id as SqUserID,
          marker: pl.marker,
          sqGameID: pl.gameId as SqGameID,
        }) as SqPlayerPick
    );
    picks = populatePicksListWithPlayerMarkers(picks, plPicks);
  });
  return picks;
};
// #endregion

// #region VERIFY & FIX GAME PICK ********************************************
export const verifyBoardIsFull = (game: SqGame) => {
  const agp = game.players; // active game players

  const numPlayers = agp.length;
  const numPicksClaimed = agp.reduce((acc, pl) => acc + pl.numberOfPicks, 0);
  const numSquares = game.picks.length;
  const numSquaresOpen = game.picks.filter((p) => typeof p === 'string');

  const msg1 = `${Emojis.eyes} Players count: %c ${numPlayers} %c`;
  const msg2 = `, squares claimed: %c ${numPicksClaimed} %c`;
  const msg3 = `, squares count %c ${numSquares} %c`;
  const msg4 = `, Squares open count %c ${numSquaresOpen} %c`;
  const msg = `${msg1}${msg2}${msg3}${msg4}`;

  console.log(`${msg}`, ValueCls, '', ValueCls, '', ValueCls, '', ValueCls, '');

  return numPicksClaimed === numSquares && numSquaresOpen.length === 100 - numSquares;
};
// #endregion

// #region GAME PLAY STATE CONFIG
export const StatesToShowRandomNumberRows = [SqGameState.GameReady, SqGameState.GameOn, SqGameState.GameComplete];

export const getDate30SecondsFromNow = (): Date => {
  const date = new Date();
  date.setSeconds(date.getSeconds() + 30);
  return date;
};
// #endregion

/**
 * Shuffles an array in place using Durstenfeld shuffle algorithm.
 *
 * @template T The type of elements in the array.
 * @param {T[]} array The array to be shuffled.
 *
 * @returns {T[]} The shuffled array.
 */
export const shuffleArray = <T>(array: T[]): T[] => {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
};